import React, { useMemo, useState } from 'react';
import Plot from 'react-plotly.js';

import { CharacteristicAnalytics as CharacteristicAnalyticsData, PcaPoint } from './types';
import {
  PLOTLY_LAYOUT_BASE,
  TimeWindowKey,
  colorForIndex,
  cutoffForWindow,
  filterByCreatedAt,
  truncate,
} from './chartHelpers';
import TimeWindowSelector from './TimeWindowSelector';
import CommentHeatmapCard from './CommentHeatmapCard';

interface Props {
  data: CharacteristicAnalyticsData;
  generatedAt: string | null;
}

const CharacteristicAnalytics: React.FC<Props> = ({ data, generatedAt }) => {
  const mostEstimated = data.most_estimated || [];
  const mostCorrected = data.most_corrected_normalized || [];
  const pca = data.pca || { points: [], explained_variance: [], sample_size: 0 };
  const [pcaWindow, setPcaWindow] = useState<TimeWindowKey>('all');

  const filteredPcaPoints = useMemo(() => {
    const cutoff = cutoffForWindow(generatedAt, pcaWindow);
    return filterByCreatedAt(pca.points || [], cutoff);
  }, [pca.points, generatedAt, pcaWindow]);

  const characteristicTraces = useMemo(() => {
    const groups = new Map<string, PcaPoint[]>();
    filteredPcaPoints.forEach(p => {
      const key = (p.characteristic_name as string) || 'unknown';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(p);
    });
    return Array.from(groups.entries()).map(([name, points], idx) => ({
      x: points.map(p => p.x),
      y: points.map(p => p.y),
      mode: 'markers' as const,
      type: 'scattergl' as const,
      name: truncate(name, 30),
      marker: {
        size: 8,
        color: colorForIndex(idx),
        symbol: points.map(p => (p.has_correction ? 'diamond' : 'circle')),
        opacity: 0.75,
      },
      hovertemplate:
        '<b>%{customdata[0]}</b><br>' +
        'sim: %{customdata[1]}<br>' +
        'situation: %{customdata[2]}<br>' +
        'corrected: %{customdata[3]}<extra></extra>',
      customdata: points.map(p => [
        p.characteristic_name || 'unknown',
        p.simulation_token || '-',
        p.situation_name || '-',
        p.has_correction ? 'yes' : 'no',
      ]),
    }));
  }, [filteredPcaPoints]);

  const explained = (pca.explained_variance || []).map(v => `${(v * 100).toFixed(1)}%`);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-3">Most commonly estimated characteristics</h3>
        {mostEstimated.length === 0 ? (
          <p className="text-sm text-gray-500">No characteristic estimations recorded yet.</p>
        ) : (
          <Plot
            data={[
              {
                type: 'bar',
                orientation: 'h',
                x: mostEstimated.slice(0, 25).map(r => r.count).reverse(),
                y: mostEstimated.slice(0, 25).map(r => truncate(r.characteristic_name, 50)).reverse(),
                marker: { color: '#2563EB' },
                hovertemplate: '%{y}<br>count: %{x}<extra></extra>',
              },
            ]}
            layout={{
              ...PLOTLY_LAYOUT_BASE,
              height: Math.max(320, Math.min(25, mostEstimated.length) * 24 + 80),
              xaxis: { title: 'Estimation count' },
              yaxis: { automargin: true },
              margin: { ...PLOTLY_LAYOUT_BASE.margin, l: 220 },
            }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
            useResizeHandler
          />
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-1">Most commonly corrected (normalised)</h3>
        <p className="text-xs text-gray-500 mb-3">
          Correction count divided by estimation count, filtered to characteristics with at least 3 estimations.
        </p>
        {mostCorrected.length === 0 ? (
          <p className="text-sm text-gray-500">No corrections have been logged yet.</p>
        ) : (
          <Plot
            data={[
              {
                type: 'bar',
                orientation: 'h',
                x: mostCorrected.slice(0, 25).map(r => r.ratio).reverse(),
                y: mostCorrected.slice(0, 25).map(r => truncate(r.characteristic_name, 50)).reverse(),
                marker: {
                  color: mostCorrected.slice(0, 25).map(r => r.correction_count).reverse(),
                  colorscale: 'Reds' as any,
                  showscale: true,
                  colorbar: { title: { text: 'corrections', side: 'right' } as any },
                },
                hovertemplate:
                  '%{y}<br>ratio: %{x:.3f}<br>corrections: %{marker.color}<extra></extra>',
              },
            ]}
            layout={{
              ...PLOTLY_LAYOUT_BASE,
              height: Math.max(320, Math.min(25, mostCorrected.length) * 24 + 80),
              xaxis: { title: 'Corrections / Estimations' },
              yaxis: { automargin: true },
              margin: { ...PLOTLY_LAYOUT_BASE.margin, l: 220 },
            }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
            useResizeHandler
          />
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <h3 className="text-lg font-semibold">PCA of characteristic estimations</h3>
          <TimeWindowSelector value={pcaWindow} onChange={setPcaWindow} />
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Structural 2308-d vector per estimation, z-scored per dimension and projected to 2D. Diamonds = has correction.
          {' The PCA basis is fitted on all '}
          <span className="font-medium text-gray-700">{(pca.points || []).length.toLocaleString()}</span>
          {' points; the selected window only filters which estimations are drawn.'}
          {explained.length === 2 && ` Explained variance: PC1 ${explained[0]}, PC2 ${explained[1]}.`}
          {' Showing '}
          <span className="font-medium text-gray-700">
            {filteredPcaPoints.length.toLocaleString()} of {(pca.points || []).length.toLocaleString()}
          </span>
          {' estimations.'}
        </p>
        {pca.warning ? (
          <p className="text-sm text-gray-500">{pca.warning}</p>
        ) : characteristicTraces.length === 0 ? (
          <p className="text-sm text-gray-500">
            {(pca.points || []).length === 0
              ? 'No PCA points available.'
              : 'No estimations fall inside the selected window.'}
          </p>
        ) : (
          <Plot
            data={characteristicTraces as any}
            layout={{
              ...PLOTLY_LAYOUT_BASE,
              height: 520,
              xaxis: { title: 'PC1', zeroline: false },
              yaxis: { title: 'PC2', zeroline: false },
              showlegend: true,
              legend: { orientation: 'v', x: 1.02, y: 1 },
            }}
            config={{ responsive: true, displaylogo: false }}
            style={{ width: '100%' }}
            useResizeHandler
          />
        )}
      </div>

      <CommentHeatmapCard
        title="Semantic heatmap of correction comments"
        description="Free-text comments left when an operator corrected a characteristic estimation, embedded with mistral-embed and projected to 2D. The blue contour shows comment density."
        payload={data.comment_heatmap || null}
        generatedAt={generatedAt}
        showRating={false}
        extraHoverFields={[
          { label: 'characteristic', pick: p => p.characteristic_name },
          { label: 'situation', pick: p => p.situation_name },
        ]}
      />
    </div>
  );
};

export default CharacteristicAnalytics;
